import { useMemo } from "react";
import { marked } from "marked";
import LegalPage from "./LegalPage";

const markdown = `# Terms of Service

*Last updated: March 2026*

## 1. Acceptance of Terms

By accessing or using ***SEND IT*** ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.

## 2. Description of Service

***SEND IT*** is a free-to-play motorsport prediction game where users predict the outcomes of head-to-head driver matchups, pole positions, and sprint podium finishes across the Formula 1 season. The Service is provided for entertainment purposes only. This is not a gambling or betting service. No real money can be wagered, won, or lost through use of the Service. Points, rankings, and league standings hold no monetary value.

## 3. Unofficial Status

***SEND IT*** is an unofficial, fan-made prediction game. We are not affiliated with, endorsed by, sponsored by, or in any way officially connected to Formula One World Championship Limited, the Fédération Internationale de l'Automobile (FIA), Formula One Management, or any Formula 1 team or driver. All motorsport-related trademarks are the property of their respective owners. For full details, please see our [Unofficial App Disclaimer](/disclaimer).

## 4. User Accounts

To use the Service, you must create an account via email or Google sign-in. You agree to:

- Provide accurate and complete information when creating your account
- Maintain the security of your account credentials
- Notify us immediately of any unauthorised access to your account
- Accept responsibility for all activities that occur under your account

You may only create and maintain one account. We reserve the right to suspend or remove duplicate accounts.

## 5. User Conduct

You agree not to:

- Use the Service for any illegal purpose
- Create multiple accounts to gain unfair advantages
- Attempt to manipulate leaderboards, scores, or league standings
- Harass, abuse, threaten, or harm other users
- Use automated systems, scripts, or bots to interact with the Service
- Interfere with or disrupt the Service, its servers, or its infrastructure
- Attempt to gain unauthorised access to any part of the Service
- Use offensive, discriminatory, or inappropriate content in display names, league names, or feedback submissions
- Share league invite codes publicly without the league owner's permission

## 6. Predictions and Scoring

You acknowledge and agree that:

- **Lock times:** Matchup and pole position predictions lock when qualifying begins for that race. Sprint podium predictions lock when sprint qualifying begins. Once locked, predictions cannot be changed.
- **Auto-save:** Predictions are saved automatically when you make a selection. There is no separate submission step.
- **Scoring:** Points are calculated automatically based on official race results. Correct matchup picks earn 1 point, correct pole position predictions earn 2 bonus points, and sprint podium predictions earn points based on accuracy.
- **Results:** We reserve the right to adjust scores in the event of errors, data discrepancies, or official post-race amendments (such as disqualifications or penalties applied after the race).
- **DNF/DNS/DSQ:** In the event that one or both drivers in a matchup do not finish, do not start, or are disqualified, we reserve the right to void the matchup or apply scoring rules at our discretion.
- **No monetary value:** Points, rankings, and league positions have no monetary value and cannot be exchanged for cash, prizes, or any other consideration.

## 7. Leagues and Social Features

When creating or joining leagues, you agree to:

- Use appropriate and non-offensive league names and descriptions
- Respect other league members
- Accept that league owners may remove members at their discretion
- Not share private league invite codes publicly without the league owner's permission

We reserve the right to remove or rename leagues that contain offensive, discriminatory, or inappropriate content.

## 8. Intellectual Property

The Service and its original content, features, design, and functionality are the property of ***SEND IT*** and are protected by applicable copyright and intellectual property laws.

You may not reproduce, distribute, modify, create derivative works of, publicly display, or otherwise exploit any part of the Service without our prior written consent.

All Formula 1-related trademarks, team names, driver names, and associated marks are the property of their respective owners and are used in this application for identification purposes only.

## 9. Data and Privacy

Your use of the Service is also governed by our [Privacy Policy](/privacy), which explains how we collect, use, and protect your personal information. By using the Service, you consent to the practices described in the Privacy Policy.

## 10. Availability and Modifications

We reserve the right to:

- Modify, suspend, or discontinue any part of the Service at any time without prior notice
- Perform maintenance that may temporarily affect availability
- Update scoring rules, matchup formats, or game mechanics between seasons

We will endeavour to notify users of significant changes where practical, but we do not guarantee uninterrupted or error-free operation of the Service.

## 11. Disclaimer of Warranties

The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.

We do not warrant that:

- The Service will be uninterrupted, secure, or error-free
- Race data, results, or session times will be accurate or timely
- Scores or leaderboards will be free from errors
- The Service will meet your specific expectations

## 12. Limitation of Liability

To the maximum extent permitted by Australian law, ***SEND IT*** and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenues, data, use, goodwill, or other intangible losses resulting from:

- Your use of or inability to use the Service
- Any errors, inaccuracies, or omissions in race data or scoring
- Unauthorised access to or alteration of your account or data
- Any conduct of other users of the Service
- Any other matter relating to the Service

## 13. Termination

We reserve the right to suspend or terminate your account and access to the Service at our sole discretion, without prior notice, for conduct that we believe:

- Violates these Terms of Service
- Is harmful to other users, to us, or to third parties
- Involves fraudulent or abusive activity

Upon termination, your right to use the Service ceases immediately. You may request deletion of your account data by contacting us at hello@sendit-app.com.

## 14. Changes to Terms

We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the updated Terms within the application and updating the "Last updated" date above. Your continued use of the Service after any changes constitutes acceptance of the new Terms.

## 15. Governing Law

These Terms shall be governed by and construed in accordance with the laws of Australia. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Australia.

## 16. Severability

If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that the remaining provisions remain in full force and effect.

## 17. Contact Us

If you have any questions about these Terms of Service, please contact us at hello@sendit-app.com.`;

export default function Terms() {
  const html = useMemo(() => marked.parse(markdown) as string, []);
  return <LegalPage title="Terms of Service" content={html} />;
}
